import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ChevronRight, ChevronLeft, Check, Car, Truck, CarFront } from 'lucide-react';
import { toast } from 'sonner';
import { SEO, generateFinancingSchema } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CreditApplicationPage = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    vehicle_type: '',
    budget: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
  });

  const vehicleTypes = [
    { id: 'auto', label: t('credit.auto'), icon: Car, image: 'https://static.prod-images.emergentagent.com/jobs/a57c2eb1-a0ed-4c79-aace-4847325febe9/images/3d068c22f95ebbbe783cb3cb151e60f89e2e333b7562be0938f680943fff1c66.png' },
    { id: 'camion', label: t('credit.truck'), icon: Truck, image: 'https://static.prod-images.emergentagent.com/jobs/a57c2eb1-a0ed-4c79-aace-4847325febe9/images/45f73e5be677e8a87b90e0e7ca8ed170c763963ff907d35632a2a7ef82a4549f.png' },
    { id: 'vus', label: t('credit.suv'), icon: CarFront, image: 'https://static.prod-images.emergentagent.com/jobs/a57c2eb1-a0ed-4c79-aace-4847325febe9/images/615d9ea2f2b00749a79566e17a1d0a1ed1fab1c992987b743df15f7603c5d70e.png' },
  ];

  const budgetOptions = [
    { id: 'under350', label: `${t('credit.under')} 350 $`, sublabel: t('credit.perMonth') },
    { id: '351-475', label: '351 $ – 475 $', sublabel: t('credit.perMonth') },
    { id: '476-600', label: '476 $ – 600 $', sublabel: t('credit.perMonth') },
    { id: '601-750', label: '601 $ – 750 $', sublabel: t('credit.perMonth') },
    { id: '751-900', label: '751 $ – 900 $', sublabel: t('credit.perMonth') },
    { id: '901plus', label: `901 $ ${t('credit.andMore')}`, sublabel: t('credit.perMonth') },
  ];

  const handleSubmit = async () => {
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.phone) {
      toast.error(t('credit.fillAllFields'));
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/credit-applications`, {
        vehicle_type: formData.vehicle_type,
        budget: formData.budget,
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
      });
      setSubmitted(true);
      toast.success(t('credit.successToast'));
    } catch (error) {
      toast.error(t('credit.errorToast'));
    } finally {
      setSubmitting(false);
    }
  };

  const canProceed = () => {
    if (step === 1) return formData.vehicle_type !== '';
    if (step === 2) return formData.budget !== '';
    if (step === 3) return formData.first_name && formData.last_name && formData.email && formData.phone;
    return false;
  };

  const nextStep = () => {
    if (canProceed() && step < 3) setStep(step + 1);
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col bg-slate-900" data-testid="credit-application-page">
        <Navbar />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="bg-white rounded-3xl p-12 max-w-lg text-center shadow-2xl">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {t('credit.successTitle')}
            </h2>
            <p className="text-slate-600 mb-8">
              {t('credit.successMessage')}
            </p>
            <div className="flex gap-4 justify-center">
              <Button asChild variant="outline">
                <Link to="/">{t('credit.backHome')}</Link>
              </Button>
              <Button asChild className="bg-[#166534] hover:bg-green-700">
                <Link to="/inventory">{t('credit.seeInventory')}</Link>
              </Button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" data-testid="credit-application-page">
      <SEO 
        title="Financement Auto - Demande de Crédit"
        description="Demande de financement auto simple et rapide. 1re, 2e et 3e chance au crédit avec 99% d'approbations. Aucun refus. Obtenez votre pré-approbation en quelques minutes chez Entrepôt Auto Montréal."
        url="https://entrepotautomontreal.ca/credit"
        keywords="financement auto montréal, crédit auto, 2e chance crédit, 3e chance crédit, prêt auto mauvais crédit, approbation garantie, financement voiture, pré-approbation auto, financement sans refus"
        schema={generateFinancingSchema()}
      />
      {/* Hero Background */}
      <div className="fixed inset-0 z-0">
        <img 
          src="https://images.unsplash.com/photo-1763165524637-9067debdc80b?w=1920&q=80"
          alt="Background"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-900/70" />
      </div>

      <div className="relative z-10">
        <Navbar />

        {/* Main Content */}
        <div className="flex-1 py-12 px-4">
          <div className="max-w-3xl mx-auto">
            {/* Header */}
            <div className="text-center mb-8">
              <p className="text-[#166534] font-semibold mb-2 text-sm tracking-wider">{t('credit.startMyRequest')}</p>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                {t('credit.heroTitle')}
              </h1>
              <p className="text-slate-300">
                {t('credit.heroSubtitle')}
              </p>
            </div>

            {/* Form Card */}
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden mx-4 sm:mx-0">
              {/* Progress Steps */}
              <div className="flex items-center justify-between px-4 sm:px-8 py-4 sm:py-6 border-b border-slate-100 overflow-x-auto">
                <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                  <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold ${
                    step >= 1 ? 'bg-[#166534] text-white' : 'bg-slate-200 text-slate-500'
                  }`}>
                    {step > 1 ? <Check className="h-3 w-3 sm:h-4 sm:w-4" /> : '1'}
                  </div>
                  <span className={`text-xs sm:text-sm font-medium whitespace-nowrap ${step >= 1 ? 'text-[#166534]' : 'text-slate-400'}`}>{t('credit.stepVehicle')}</span>
                </div>
                <div className="flex-1 h-1 bg-slate-200 mx-2 sm:mx-4 rounded min-w-[20px]">
                  <div className={`h-full bg-[#166534] rounded transition-all ${step >= 2 ? 'w-full' : 'w-0'}`} />
                </div>
                <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                  <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold ${
                    step >= 2 ? 'bg-[#166534] text-white' : 'bg-slate-200 text-slate-500'
                  }`}>
                    {step > 2 ? <Check className="h-3 w-3 sm:h-4 sm:w-4" /> : '2'}
                  </div>
                  <span className={`text-xs sm:text-sm font-medium whitespace-nowrap ${step >= 2 ? 'text-[#166534]' : 'text-slate-400'}`}>{t('credit.stepBudget')}</span>
                </div>
                <div className="flex-1 h-1 bg-slate-200 mx-2 sm:mx-4 rounded min-w-[20px]">
                  <div className={`h-full bg-[#166534] rounded transition-all ${step >= 3 ? 'w-full' : 'w-0'}`} />
                </div>
                <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                  <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold ${
                    step >= 3 ? 'bg-[#166534] text-white' : 'bg-slate-200 text-slate-500'
                  }`}>
                    3
                  </div>
                  <span className={`text-xs sm:text-sm font-medium whitespace-nowrap ${step >= 3 ? 'text-[#166534]' : 'text-slate-400'}`}>{t('credit.stepContact')}</span>
                </div>
              </div>

              {/* Form Content */}
              <div className="p-4 sm:p-8">
                {/* Step 1: Vehicle Type */}
                {step === 1 && (
                  <div>
                    <p className="text-slate-600 mb-6 text-sm sm:text-base">{t('credit.chooseCategory')}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                      {vehicleTypes.map((type) => (
                        <button
                          key={type.id}
                          onClick={() => setFormData({ ...formData, vehicle_type: type.id })}
                          className={`relative rounded-xl border-2 p-3 sm:p-4 transition-all hover:border-[#166534] ${
                            formData.vehicle_type === type.id 
                              ? 'border-[#166534] bg-green-50' 
                              : 'border-slate-200'
                          }`}
                        >
                          <div className={`absolute top-2 right-2 sm:top-3 sm:right-3 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            formData.vehicle_type === type.id 
                              ? 'border-[#166534] bg-[#166534]' 
                              : 'border-slate-300'
                          }`}>
                            {formData.vehicle_type === type.id && <Check className="h-3 w-3 text-white" />}
                          </div>
                          <img 
                            src={type.image} 
                            alt={type.label}
                            className="w-full h-24 sm:h-32 object-cover rounded-lg mb-2 sm:mb-3"
                          />
                          <p className="font-semibold text-slate-900 text-sm sm:text-base">{type.label}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 2: Budget */}
                {step === 2 && (
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-6" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                      {t('credit.whatBudget')}
                    </h3>
                    <div className="grid grid-cols-3 gap-4">
                      {budgetOptions.map((option) => (
                        <button
                          key={option.id}
                          onClick={() => setFormData({ ...formData, budget: option.id })}
                          className={`relative rounded-xl border-2 p-5 transition-all hover:border-[#166534] text-left ${
                            formData.budget === option.id 
                              ? 'border-[#166534] bg-green-50' 
                              : 'border-slate-200'
                          }`}
                        >
                          <div className={`absolute top-3 right-3 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            formData.budget === option.id 
                              ? 'border-[#166534] bg-[#166534]' 
                              : 'border-slate-300'
                          }`}>
                            {formData.budget === option.id && <Check className="h-3 w-3 text-white" />}
                          </div>
                          <p className="font-semibold text-slate-900">{option.label}</p>
                          <p className="text-sm text-[#166534]">{option.sublabel}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 3: Contact Info */}
                {step === 3 && (
                  <div>
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t('credit.firstName')} <span className="text-red-500">*</span>
                        </label>
                        <Input
                          value={formData.first_name}
                          onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                          placeholder={t('credit.firstNamePlaceholder')}
                          className="h-12 border-slate-200"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t('credit.lastName')} <span className="text-red-500">*</span>
                        </label>
                        <Input
                          value={formData.last_name}
                          onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                          placeholder={t('credit.lastNamePlaceholder')}
                          className="h-12 border-slate-200"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t('credit.email')} <span className="text-red-500">*</span>
                        </label>
                        <Input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="votre@email.com"
                          className="h-12 border-slate-200"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          {t('credit.phone')} <span className="text-red-500">*</span>
                        </label>
                        <Input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="(514) 555-1234"
                          className="h-12 border-slate-200"
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Navigation Buttons */}
              <div className="px-8 py-6 bg-slate-50 flex items-center justify-between">
                <button
                  onClick={prevStep}
                  className={`flex items-center gap-2 text-slate-600 hover:text-slate-900 font-medium ${
                    step === 1 ? 'invisible' : ''
                  }`}
                >
                  <ChevronLeft className="h-4 w-4" />
                  {t('credit.previous')}
                </button>
                
                {step < 3 ? (
                  <Button
                    onClick={nextStep}
                    disabled={!canProceed()}
                    className="bg-[#166534] hover:bg-green-700 px-8"
                  >
                    {t('credit.continue')}
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmit}
                    disabled={!canProceed() || submitting}
                    className="bg-[#166534] hover:bg-green-700 px-8"
                  >
                    {submitting ? t('credit.submitting') : t('credit.submit')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
};

export default CreditApplicationPage;
